import { useEffect, useState } from "react";
import { Loader2, FileText, Download } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface InvoiceRow {
  id: string;
  invoice_number: string;
  issued_at: string;
  currency: string;
  total: number;
  payment_provider: string;
}

export function InvoicesCard() {
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("invoices")
        .select("id, invoice_number, issued_at, currency, total, payment_provider")
        .order("issued_at", { ascending: false });
      if (!cancelled) {
        setInvoices((data as InvoiceRow[]) || []);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleDownload = async (id: string) => {
    setDownloadingId(id);
    try {
      const { data, error } = await supabase.functions.invoke("get-invoice-download-url", {
        body: { invoiceId: id },
      });
      if (error || !data?.url) throw new Error(error?.message || "Could not get download link");
      window.open(data.url, "_blank");
    } catch (err) {
      toast({
        title: "Download failed",
        description: err instanceof Error ? err.message : "Could not download invoice",
        variant: "destructive",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const fmt = (n: number, c: string) =>
    (c === "INR" ? "\u20B9" : "$") + n.toLocaleString(c === "INR" ? "en-IN" : "en-US",
      { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <Card className="rounded-none border-0 border-t border-[var(--sn-border)] bg-transparent px-0 py-6 shadow-none">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-5 h-5 text-accent" />
        <h3 className="text-lg font-bold text-foreground">Tax Invoices</h3>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading invoices...
        </div>
      ) : invoices.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No invoices yet. They will appear here after your next payment.
        </p>
      ) : (
        <div className="space-y-2">
          {invoices.map((inv) => (
            <div
              key={inv.id}
              className="flex items-center justify-between p-3 rounded-lg border border-border/50 bg-background/30"
            >
              <div className="min-w-0">
                <p className="font-mono text-sm text-foreground truncate">{inv.invoice_number}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(inv.issued_at).toLocaleDateString("en-GB", {
                    day: "2-digit", month: "short", year: "numeric",
                  })}
                  {" \u00B7 "}
                  {fmt(Number(inv.total), inv.currency)}
                  {" \u00B7 "}
                  {inv.payment_provider}
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDownload(inv.id)}
                disabled={downloadingId === inv.id}
              >
                {downloadingId === inv.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <><Download className="w-4 h-4 mr-1" /> PDF</>
                )}
              </Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}